/* React */
import React, { useCallback, useEffect } from "react";

/* React-Redux */
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  clearOptions,
  setOptions,
} from "src/redux/features/insights-filter/insightsFilterOptionsSlice";

/* Thunks */
import { fetchSurvivalAnalysis } from "src/redux/thunks/survival-analysis/survivalAnalysisThunk";

/* Utils */
import useChartLoadingScreen from "src/utils/hooks/useChartLoadingScreen";
import { useFetchWithMainCondition } from "src/utils/hooks/useFetchWithMainCondition";
import { useIsCustomCohortActive } from "src/utils/hooks/useIsCustomCohortActive";
import useFilterCleanup from "src/utils/hooks/useFilterCleanup";
import ChartLayoutWrapper from "src/utils/wrapper/chart-layout-wrapper/ChartLayoutWrapper";

/* Layout */
import { getStackedLayout } from "src/utils/layout/stacked-layout/getStackedLayout";

/* Interfaces */
import { BaseParams } from "src/shared/interfaces/charts/FetchWithMainCondition";
import { ChartType } from "src/shared/constants/charts/Chart";

const SurvivalAnalysis: React.FC = () => {
  const dispatch = useDispatch();
  useFilterCleanup();

  const insightsFilterState = useSelector(
    (state: RootState) => state.insightsFilter
  );

  const { cohorts } = insightsFilterState;

  const { mainCondition } = insightsFilterState;

  const survivalAnalysisState = useSelector(
    (state: RootState) => state.survivalAnalysis
  );

  const survivalAnalysisOptionsState = useSelector(
    (state: RootState) => state.survivalAnalysisOptions
  );

  const { startPoint, showNoValueEntries } =
    survivalAnalysisOptionsState;

  const { data, isLoading, modifiedAt, error } =
    survivalAnalysisState;

  const customCohortsState = useSelector(
    (state: RootState) => state.customCohorts
  );

  // A custom cohort has no well-defined completeness denominator, so the toggle
  // is disabled there and no-value entries are hidden regardless of a stale
  // slice value.
  const isCustomCohortActive = useIsCustomCohortActive();

  useChartLoadingScreen(isLoading, modifiedAt);

  // useCallback is required to prevent infinite loop.
  const buildParams = useCallback(
    (baseParams: BaseParams) => ({
      ...baseParams,
      startPoint: startPoint.value!,
    }),
    [startPoint.value]
  );

  useFetchWithMainCondition({
    mainCondition,
    debouncedValue: insightsFilterState,
    customCohortsState,
    fetchAction: fetchSurvivalAnalysis,
    buildParams,
  });

  useEffect(() => {
    dispatch(setOptions(ChartType.SCATTER));
    return () => {
      dispatch(clearOptions());
    };
  }, [dispatch]);

  const tmpTypes = data?.types ?? [];

  const layout = getStackedLayout(
    tmpTypes,
    data!,
    cohorts.value,
    showNoValueEntries && !isCustomCohortActive
  );

  return (
    <ChartLayoutWrapper layout={layout} error={error} />
  );
};

export default SurvivalAnalysis;
